// ---------------------------------------------------------------------------
// Auth state management
// Stores only the authenticated user profile (NOT the JWT — it lives in an
// HttpOnly cookie managed by the browser and backend).
//
// On page load, the user profile is restored from sessionStorage for a fast
// initial render.  App.tsx calls apiv2.me() to validate the cookie is still
// valid; if it returns 401 the auth state is cleared automatically.
// ---------------------------------------------------------------------------

import { useState, useEffect, useCallback } from "react";

// The backend returns the real RBAC role name verbatim (platform_admin,
// conference_attendee, data_engineer, ... — see /admin/roles for the full
// set), so this is a plain string rather than a fixed union that would
// need to be kept in sync by hand. `admin` and `platform_admin` are both
// treated as full-admin by `useAuth.isAdmin`.
export type UserRole = string;

export interface AuthUser {
  id: string;
  email: string;
  full_name: string;
  role: UserRole;
  /** Role's actual permission codenames (e.g. "admin:project"), or the
   * platform_admin wildcard ["*"]. Populated by /auth/login, /auth/me,
   * and /auth/refresh. */
  permissions: string[];
  is_active: boolean;
  must_change_password: boolean;
}

export interface AuthState {
  user: AuthUser | null;
}

const STORAGE_KEY = "ztd_user";

function loadUser(): AuthUser | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as AuthUser;
  } catch {
    return null;
  }
}

function saveUser(user: AuthUser | null): void {
  if (user) {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(user));
  } else {
    sessionStorage.removeItem(STORAGE_KEY);
  }
}

// Global listeners for auth state changes
type AuthListener = (state: AuthState) => void;
const listeners: AuthListener[] = [];
let currentAuth: AuthState = { user: loadUser() };

function notify() {
  listeners.forEach((fn) => fn(currentAuth));
}

export function setUser(user: AuthUser) {
  currentAuth = { user };
  saveUser(user);
  notify();
}

export function clearAuth() {
  currentAuth = { user: null };
  saveUser(null);
  notify();
}

export function getAuth(): AuthState {
  return currentAuth;
}

/** @deprecated Token is now in an HttpOnly cookie. Use credentials:'include' on fetches instead. */
export function getToken(): string | null {
  return null;
}

/** React hook - re-renders when auth changes. */
export function useAuth() {
  const [state, setState] = useState<AuthState>(currentAuth);

  useEffect(() => {
    const listener: AuthListener = (s) => setState({ ...s });
    listeners.push(listener);
    return () => {
      const idx = listeners.indexOf(listener);
      if (idx >= 0) listeners.splice(idx, 1);
    };
  }, []);

  const isLoggedIn = !!state.user;
  const isAdmin =
    state.user?.role === "admin" || state.user?.role === "platform_admin";
  // Real permission check, not a hardcoded role-name string -- no role in
  // this system's RBAC seed is actually named "project_user"; any role
  // (e.g. conference_attendee) can be granted the "admin:project" grain.
  const permissions = state.user?.permissions ?? [];
  const isProjectUser =
    isAdmin ||
    permissions.includes("*") ||
    permissions.includes("admin:*") ||
    permissions.includes("admin:tenant") ||
    permissions.includes("admin:project");
  const isGeneralUser = !!state.user; // any logged-in user

  // Generic single-codename check, for actions gated on a specific
  // permission rather than one of the broad role buckets above (e.g.
  // "project:purge_data", which — deliberately — no role gets by default
  // except via the "*" wildcard; see #680 phase 3/4).
  const hasPermission = useCallback(
    (codename: string) =>
      permissions.includes("*") || permissions.includes(codename),
    [permissions],
  );

  const logout = useCallback(() => clearAuth(), []);

  return {
    ...state,
    isLoggedIn,
    isAdmin,
    isProjectUser,
    isGeneralUser,
    hasPermission,
    logout,
  };
}
